import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { useCompany } from '../contexts/CompanyContext';
import { 
  HomeIcon, 
  ClipboardDocumentListIcon, 
  TruckIcon, 
  UsersIcon,
  UserGroupIcon,
  ChartBarIcon,
  DocumentTextIcon,
  Cog6ToothIcon,
  PowerIcon,
  Bars3Icon,
  XMarkIcon,
  BellIcon,
  UserCircleIcon,
  ClockIcon,
  BuildingOfficeIcon
} from '@heroicons/react/24/outline';
import { cn } from '../utils';
import CompanyBranding from './CompanyBranding';
import LanguageSelector from './LanguageSelector';

const Layout = ({ children }) => {
  const { user, logout, isAdmin } = useAuth();
  const { company, loading: companyLoading } = useCompany();
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const navigation = [
    { name: t('navigation.dashboard'), href: '/dashboard', icon: HomeIcon },
    { name: t('navigation.tasks'), href: '/tasks', icon: ClipboardDocumentListIcon },
    { name: t('navigation.cars'), href: '/cars', icon: TruckIcon },
    { name: t('navigation.attendance'), href: '/attendance', icon: ClockIcon },
    ...(isAdmin ? [
      { name: t('navigation.users'), href: '/users', icon: UsersIcon },
      { name: t('navigation.groups'), href: '/groups', icon: UserGroupIcon },
      { name: t('navigation.templates'), href: '/templates', icon: DocumentTextIcon },
      { name: t('navigation.analytics'), href: '/analytics', icon: ChartBarIcon },
      { name: t('navigation.companySettings'), href: '/company-settings', icon: BuildingOfficeIcon },
    ] : []),
    { name: t('navigation.settings'), href: '/settings', icon: Cog6ToothIcon },
  ];

  return (
    <div className="min-h-screen bg-dark-900">
      {/* Mobile sidebar */}
      <div className={cn(
        'fixed inset-0 z-50 lg:hidden',
        sidebarOpen ? 'block' : 'hidden'
      )}>
        <div className="fixed inset-0 bg-gray-600 bg-opacity-75" onClick={() => setSidebarOpen(false)} />
        <div className="fixed inset-y-0 left-0 flex w-64 flex-col bg-dark-800 border-r border-dark-700">
          <div className="flex h-16 items-center justify-between px-4">
            <CompanyBranding size="default" />
            <button
              onClick={() => setSidebarOpen(false)}
              className="text-dark-300 hover:text-white"
            >
              <XMarkIcon className="h-6 w-6" />
            </button>
          </div>
          <nav className="flex-1 px-4 py-4 space-y-2">
            {navigation.map((item) => {
              const isActive = location.pathname === item.href;
              return (
                <Link
                  key={item.name}
                  to={item.href}
                  className={cn(
                    'sidebar-link',
                    isActive ? 'sidebar-link-active' : 'sidebar-link-inactive'
                  )}
                  onClick={() => setSidebarOpen(false)}
                >
                  <item.icon className="h-5 w-5 mr-3" />
                  {item.name}
                </Link>
              );
            })}
          </nav>
          <div className="p-4 border-t border-dark-700">
            <button
              onClick={handleLogout}
              className="sidebar-link sidebar-link-inactive w-full"
            >
              <PowerIcon className="h-5 w-5 mr-3" />
              {t('navigation.logout')}
            </button>
          </div>
        </div>
      </div>

      {/* Desktop sidebar */}
      <div className="hidden lg:fixed lg:inset-y-0 lg:z-50 lg:flex lg:w-64 lg:flex-col">
        <div className="flex grow flex-col gap-y-5 overflow-y-auto bg-dark-800 border-r border-dark-700 px-6">
          <div className="flex h-16 shrink-0 items-center">
            <CompanyBranding size="default" />
          </div>
          <nav className="flex flex-1 flex-col">
            <ul role="list" className="flex flex-1 flex-col gap-y-7">
              <li>
                <ul role="list" className="-mx-2 space-y-1">
                  {navigation.map((item) => {
                    const isActive = location.pathname === item.href;
                    return (
                      <li key={item.name}>
                        <Link
                          to={item.href}
                          className={cn(
                            'sidebar-link',
                            isActive ? 'sidebar-link-active' : 'sidebar-link-inactive'
                          )}
                        >
                          <item.icon className="h-5 w-5 mr-3" />
                          {item.name}
                        </Link>
                      </li>
                    );
                  })}
                </ul>
              </li>
              <li className="mt-auto">
                <button
                  onClick={handleLogout}
                  className="sidebar-link sidebar-link-inactive w-full"
                >
                  <PowerIcon className="h-5 w-5 mr-3" />
                  {t('navigation.logout')}
                </button>
              </li>
            </ul>
          </nav>
        </div>
      </div>

      <div className="lg:pl-64">
        {/* Top bar */}
        <div className="sticky top-0 z-40 flex h-16 shrink-0 items-center gap-x-4 border-b border-dark-700 bg-dark-800 px-4 shadow-sm sm:gap-x-6 sm:px-6 lg:px-8">
          <button
            type="button"
            className="-m-2.5 p-2.5 text-dark-300 lg:hidden"
            onClick={() => setSidebarOpen(true)}
          >
            <span className="sr-only">Open sidebar</span>
            <Bars3Icon className="h-6 w-6" aria-hidden="true" />
          </button>

          <div className="flex flex-1 gap-x-4 self-stretch lg:gap-x-6">
            <div className="flex flex-1 items-center">
              <h1 className="text-lg font-semibold text-white">
                {navigation.find(item => item.href === location.pathname)?.name || 'Dashboard'}
              </h1>
            </div>
            <div className="flex items-center gap-x-4 lg:gap-x-6">
              <LanguageSelector variant="compact" />
              
              <button
                type="button"
                className="-m-2.5 p-2.5 text-dark-300 hover:text-white"
              >
                <span className="sr-only">View notifications</span>
                <BellIcon className="h-6 w-6" aria-hidden="true" />
              </button>

              <div className="hidden lg:block lg:h-6 lg:w-px lg:bg-dark-600" aria-hidden="true" />

              <div className="flex items-center gap-x-3">
                <div className="flex items-center gap-x-2">
                  <UserCircleIcon className="h-8 w-8 text-dark-300" />
                  <div className="hidden sm:block">
                    <p className="text-sm font-medium text-white">{user?.name}</p>
                    <p className="text-xs text-dark-400 capitalize">{user?.role}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Main content */}
        <main className="py-10">
          <div className="px-4 sm:px-6 lg:px-8">
            {children}
          </div>
        </main>
      </div>
    </div>
  );
};

export default Layout;